import {
    AfterViewInit,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {ContentDirective} from '../../../shared/directives/content.directive';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, delay, filter, takeUntil} from 'rxjs/operators';
import {Operator, Sort, UrlParameter} from '../../../shared/utils/url.builder';
import {SearchService} from '../../../store/core/service/search.service';
import {entityMetadata} from '../../../store/entity/entity-metadata';
import {MatSelect} from '@angular/material/select';
import {
    AbstractSelectComponent,
    NewItemModalOptions
} from '../../../modules/core/component/select/abstract-select.component';
import {AbstractSearchService} from '../../../modules/core/service/v2/service/search/abstractSearch.service';
import {AbstractFindOneByIdService} from '../../../modules/core/service/v2/service/findone/abstractFindOneById.service';
import {UrlParameterBuilder} from '../../../modules/core/http/UrlParameterBuilder';
import {FormUtils} from '../../../shared/utils/form.utils';

@Component({
    selector: 'app-select-search-data',
    templateUrl: './select-search-data.component.html',
    styleUrls: ['./select-search-data.component.scss'],
})
export class SelectSearchDataComponent<T> extends AbstractSelectComponent<T> implements OnInit, OnDestroy, AfterViewInit, OnChanges {
    @ContentChild(ContentDirective, {read: TemplateRef}) template: TemplateRef<any>;

    @Input() newItemModalOptions: NewItemModalOptions<any, any, any>;
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() parentFormControlName: string;
    /** Label to be shown when no entries are found */
    @Input() noEntriesFoundLabel: string;
    @Input() placeHolderLabel: string;
    @Input() filteredServerSideElements$;
    @Input() eraseSelectionEnabled = false;

    @Input() parameterSearch = undefined;
    @Input() additionalUrlParameters: UrlParameter[] = [];
    @Input() sort: Sort = undefined;
    @Input() pageSize = 10;

    @Input() showNewItem = false;
    @Input() newItemLabel = 'Créer';
    @Input() searchOnInit = false;
    @Input() searchTerm: string;
    @Input() automaticSelectIfOneResult = false;

    // SearchService<T, any> is deprecated use AbstractSearchService<any, T> instead
    @Input() service: SearchService<T, any> | AbstractSearchService<any, T>;

    // Deprecated use value instead
    @Input() findOneByIdService: AbstractFindOneByIdService<any, T>;
    @Input() value: T;

    @Input() errorsMessages: { [key: string]: string };


    @Output() itemSelectEvent = new EventEmitter<any>();
    @Output() newItemClickEvent = new EventEmitter<any>();
    @Output() itemClickEvent = new EventEmitter<any>();

    @ViewChild('matSelect') matSelect: MatSelect;

    subscriptions: Subscription[] = [];

    /** control for filter for server side. */
    public filterCtrl: UntypedFormControl = new UntypedFormControl();

    /** indicate search operation is in progress */
    public searching = false;
    elements: T[] = [];
    /** Subject that emits when the component has been destroyed. */
    protected _onDestroy = new Subject<void>();

    constructor() {
        super();
    }

    click() {
    }

    ngOnInit() {
        // this.filteredServerSideElements$ = this.service.page$.pipe(map(value => value ? value.content : []));

        // listen for search field value changes
        this.filterCtrl.valueChanges
            .pipe(
                filter(search => !!search),
                debounceTime(200),
                delay(500),
                takeUntil(this._onDestroy)
            )
            .subscribe(search => {
                this.search(search);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const valueChange = changes['value'];
        if (valueChange && this.value) {
            this.addElement(this.value);
            const id = this.extractValue(this.value);
            this.elementControl.setValue(id);
        }

        const searchTermChange = changes['searchTerm'];
        if (searchTermChange && !searchTermChange.firstChange && this.searchTerm && this.searchTerm.length > 0) {
            this.filterCtrl.setValue(this.searchTerm);
        }
    }


    ngAfterViewInit(): void {
        this.subscriptions.push(this.service.loading$.subscribe(loading => this.searching = loading));

        this.subscriptions.push(this.service.page$.subscribe(page => {
            const newElements: T[] = [];
            page?.content?.forEach(value1 => newElements.push(value1));

            if (!this.elements || JSON.stringify(this.elements) !== JSON.stringify(newElements)) {
                this.clearElements();
                newElements.forEach(value1 => {
                    this.addElement(value1);
                });
            }
            if (this.value) {
                this.addElement(this.value);
            }
            if (this.elements.length === 1 && this.automaticSelectIfOneResult) {
                this.setValue(this.elements[0]);
            }
        }));

        this.subscriptions.push(this.service.loading$.subscribe(loading => {
            this.searching = loading;
        }));

        const value = this.elementControl?.value;
        if (value && !this.value) {
            this.setValueById(value);
        }

        const hasSearchTerm = this.searchTerm && this.searchTerm.length > 0;
        if (hasSearchTerm) {
            this.filterCtrl.setValue(this.searchTerm);
        }

        if (this.searchOnInit === true || hasSearchTerm) {
            this.search(this.filterCtrl.value);
        }

    }

    private addElement(element: T) {
        if (this.elements.findIndex(value2 => this.extractValue(value2) === this.extractValue(element)) < 0) {
            this.elements.push(element);
        }
    }


    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    search(term) {
        this.searching = true;
        this.elements = [];

        // Clear value
        //  this.elementControl?.setValue(undefined);
        this.clearElements();


        // console.logByType['search', this.elementControl.value, this.elements]);

        this.service.cancel();

        let parameters = [];
        if (this.parameterSearch) {
            parameters = new UrlParameterBuilder().addParameter(this.parameterSearch, term, Operator.LIKE)
                .parameters;
        }
        if (this.additionalUrlParameters) {
            parameters.push(...this.additionalUrlParameters);
        }
        if (this.service instanceof SearchService) {
            this.service.firstPage(parameters, 10, this.sort);
        } else {
            this.service.search(term, this.parameterSearch, this.sort, this.pageSize, this.additionalUrlParameters);
        }

    }

    private clearElements() {
        let currentElement;
        if (this.elementControl.value) {
            currentElement = this.elements.find(element => this.extractValue(element) === this.elementControl.value);
        }
        this.elements = [];

        if (currentElement) {
            this.addElement(currentElement);
        }
    }

    newItemSelected() {
        this.matSelect.close();

        if (this.newItemModalOptions) {
            super.newItemSelected(this.newItemModalOptions);
        } else if (this.newItemClickEvent) {
            this.newItemClickEvent.emit();
        }
    }

    itemSelected(element: any): any {
        this.matSelect.close();
        if (this.itemSelectEvent && element) {
            this.itemSelectEvent.emit(element);
        }
    }

    get elementControl() {
        return this.parentFormGroup.get(this.parentFormControlName);
    }

    extractValue(element: any): any {
        // console.log(['extractValue', element]);
        if (element) {
            if (this.service instanceof SearchService) {
                // console.log(['service', element, this.service, this.service.entityName, entityMetadata[this.service.entityName]]);
                return entityMetadata[this.service.entityName].selectId(element);
            } else {
                return this.service.getEntityId(element);
            }
        }
    }

    setValueById(id: any, emitEvent = true): Promise<T> {
        return new Promise((resolve, reject) => {
            if (!id) {
                this.elementControl.setValue(undefined, {emitEvent});
                this.elements = [];
                resolve(undefined);
            } else {
                /* if (this.service instanceof SearchService) {
                    this.service.getByKey(id).toPromise()
                        .then(value1 => {
                            this.elements = [value1];
                            this.elementControl.setValue(this.extractValue(value1), {emitEvent});
                        });
                } else { */
                this.findOneByIdService.loadEntity(id).then(value1 => {
                    this.elements = [value1];
                    this.elementControl.setValue(this.extractValue(value1), {emitEvent});
                });
                // }
            }
        });
    }

    setValue(value: T, emitEvent = true){
        console.log(['setValue', value, this.extractValue(value)]);
        this.elements = [value];
        this.elementControl.setValue(this.extractValue(value), {emitEvent});
    }

    entityLoaded(entity: T, emitEvent = true) {
        if (!entity) {
            this.elementControl.setValue(undefined, {emitEvent});
            this.elements = [];
        } else {
            this.elementControl.setValue(this.extractValue(entity), {emitEvent});
            this.elements = [entity];
        }
    }

    eraseSelection() {
        this.filterCtrl.setValue(null);
        this.elementControl.setValue(null);
        this.elements = [];
    }

    getSelectedElement(): any {
        if (this.elementControl.value) {
            const element = this.elements
                .filter(item => this.extractValue(item) === this.elementControl.value);
            if (element && element.length === 1) {
                return element[0];
            }
        }
        return undefined;
    }

    addNewItem(value: any) {
        console.log(['addNewItem', value]);
        if (value) {
            // console.log(['addNewItem', value]);
            this.value = value;
            this.setValue(value, true);
            this.addElement(value);
            this.itemSelectEvent.emit(value);
        }
    }

    closeSelect() {
        this.matSelect.close();
    }

    openSelect() {
        this.matSelect.open();
    }

    getSelectInputValue(): string {
        return this.filterCtrl.value;
    }

    errorsKey(): string[] {
        const errors = FormUtils.collectErrors(this.elementControl);
        if (errors) {
            return Object.keys(errors);
        }
    }

    extractErrorMessage(errorKey: string) {
        if (this.errorsMessages && this.errorsMessages[errorKey]) {
            return this.errorsMessages[errorKey];
        }
        if (errorKey === 'required') {
            return 'La valeur est obligatoire';
        }
        return 'La valeur n\'est pas correcte';
    }
}
